/**
 * Shared coordination state for back-button handling across useBackButtonClose
 * and the global explore guard.
 *
 * When useBackButtonClose calls window.history.back() as part of its cleanup
 * (programmatic close of an overlay), it increments `cleanupBackInFlight` so
 * that other popstate listeners know to ignore the resulting popstate event.
 *
 * `activeOverlayCount` tracks how many overlays are currently open and managed
 * by useBackButtonClose. The global guard skips navigating to Explore when any
 * overlay is active (the overlay handler takes care of that back press).
 *
 * Deferred drain for the cleanup-back signal:
 *   A single popstate event is dispatched synchronously to EVERY listener
 *   registered on `window` (the App-root explore guard, HomePage's own
 *   popstate handler, etc.), in listener-registration order. If the signal
 *   were consumed (decremented) by whichever listener happens to run first,
 *   every listener that runs later in that same dispatch would see the
 *   counter already back at 0 and would wrongly treat the programmatic
 *   cleanup pop as a real back press — a listener-ordering race.
 *
 *   To avoid this, `peekCleanupBack()` never decrements synchronously. The
 *   first call that observes the signal (per popstate dispatch) schedules a
 *   single deferred decrement (via a macrotask), so the counter only drops
 *   once the current popstate has finished propagating to every listener.
 *   Any further calls before that scheduled decrement runs just observe
 *   `true` without scheduling a second one, so the drain never over-fires.
 */

/** How many history.back() calls are currently in-flight from overlay cleanup. */
let cleanupBackInFlight = 0;

/** Whether a deferred decrement of `cleanupBackInFlight` is already queued. */
let drainScheduled = false;

/**
 * How many useBackButtonClose overlays are currently open.
 * Incremented when isOpen becomes true, decremented when it becomes false.
 */
let activeOverlayCount = 0;

/**
 * Call before window.history.back() in overlay cleanup to signal that the
 * resulting popstate should not trigger navigation to Explore.
 */
export function incrementCleanupBack(): void {
  cleanupBackInFlight++;
}

/**
 * Non-consuming, non-starving check for an in-flight cleanup history.back().
 * A single popstate event reaches multiple listeners synchronously; this
 * returns `true` to ALL of them for the same signal instead of only the
 * first caller. The underlying counter is decremented on a deferred
 * (macrotask) basis, so it clears itself once the current dispatch is done
 * without starving listeners that run later in the same dispatch. Safe to
 * call from any number of listeners — idempotent per outstanding signal.
 */
export function peekCleanupBack(): boolean {
  if (cleanupBackInFlight <= 0) return false;
  if (!drainScheduled) {
    drainScheduled = true;
    setTimeout(() => {
      cleanupBackInFlight = Math.max(0, cleanupBackInFlight - 1);
      drainScheduled = false;
    }, 0);
  }
  return true;
}

/** Call when a useBackButtonClose overlay becomes open (isOpen = true). */
export function incrementActiveOverlays(): void {
  activeOverlayCount++;
}

/** Call when a useBackButtonClose overlay becomes closed (isOpen = false). */
export function decrementActiveOverlays(): void {
  activeOverlayCount = Math.max(0, activeOverlayCount - 1);
}

/**
 * Returns true when at least one useBackButtonClose overlay is currently open.
 * The global explore guard uses this to skip navigation when an overlay is
 * handling the back press.
 */
export function hasActiveOverlays(): boolean {
  return activeOverlayCount > 0;
}

/**
 * Non-consuming check for an in-flight cleanup history.back().
 * Alias of `peekCleanupBack` — kept as a separate, descriptively-named export
 * for call sites (e.g. HomePage's own popstate handler) that only need to
 * stay passive during a cleanup pop. Calling it is equally safe for arming
 * the deferred drain, since `peekCleanupBack` is idempotent per signal.
 */
export function isCleanupBackInFlight(): boolean {
  return peekCleanupBack();
}
